<script lang="ts" setup>
import {computed, ref, watch} from 'vue';
import {debounce} from 'throttle-debounce';
import {GET} from '../modules/fetch.ts';
import DevopsDashboardFilterCombo, {type ComboOption} from './DevopsDashboardFilterCombo.vue';
import {
  RANGE_PRESETS,
  resolvePresetRange,
  isCustomRangeValid,
  visibleFilterFields,
  topSelectOptions,
  type FilterScope,
  type FilterValue,
  type RangePreset,
} from './DevopsDashboardFilterModel.ts';

type ComboKind = 'org' | 'team' | 'repo' | 'user';

const props = defineProps<{
  scopeType: FilterScope;
  filtersUrl: string;
  maxRangeDays: number;
  topOptions: number[];
  topLimitCap: number;
  expanded: boolean;
  modelValue: FilterValue;
  locale: Record<string, string>;
}>();

const emit = defineEmits<{
  apply: [value: FilterValue];
  reset: [];
}>();

const draft = ref<FilterValue>({...props.modelValue});

// Re-seed the draft from the currently applied filters whenever the panel is (re)opened,
// so an abandoned edit from a previous session doesn't linger around.
watch(() => props.expanded, (expanded) => {
  if (expanded) draft.value = {...props.modelValue};
});

// If the applied filters change from the outside (reset, URL restore) while the panel is
// collapsed, keep the draft in sync so re-opening it shows the actual current state. This also
// covers Reset while the panel is still expanded (see `onResetFilters` in DevopsDashboard.vue,
// which never collapses the panel): without re-seeding here too, the draft would keep the
// pre-reset values, and a subsequent Apply would silently re-apply them right over the reset.
watch(() => props.modelValue, (value) => {
  draft.value = {...value};
});

const visibleFields = computed(() => visibleFilterFields(props.scopeType));
const showOrgFilter = computed(() => visibleFields.value.org);
const showTeamFilter = computed(() => visibleFields.value.team);
const showRepoFilter = computed(() => visibleFields.value.repo);
const showUserFilter = computed(() => visibleFields.value.user);
// Only the "global" scope has an org selector at all, so "needs an org first" only
// applies there; org/repo-scoped pages already have their scope fixed by the route.
const teamDisabled = computed(() => showOrgFilter.value && !draft.value.orgId);

const rangePresetLocaleKeys: Record<RangePreset, string> = {
  last7d: 'rangeLast7d',
  last30d: 'rangeLast30d',
  last90d: 'rangeLast90d',
  thisMonth: 'rangeThisMonth',
  thisQuarter: 'rangeThisQuarter',
  lastYear: 'rangeLastYear',
  custom: 'rangeCustom',
};

const topAllLabel = computed(() => props.locale.topAll.replace('{count}', String(props.topLimitCap)));

// The admin-configured default top-N can be any positive integer, not just one of the preset
// counts offered below; make sure it's always a selectable (and thus visibly selected) option.
const topOptionsForSelect = computed(() => topSelectOptions(props.topOptions, draft.value.top));

const isRangeValid = computed(() => {
  if (draft.value.rangePreset !== 'custom') return true;
  return isCustomRangeValid(draft.value.since, draft.value.until, props.maxRangeDays);
});

function onRangePresetChange() {
  if (draft.value.rangePreset === 'custom') {
    // Seed the custom inputs with a sensible range instead of leaving them blank.
    if (!draft.value.since || !draft.value.until) {
      const resolved = resolvePresetRange('last30d', new Date());
      draft.value.since = resolved.since;
      draft.value.until = resolved.until;
    }
    return;
  }
  const resolved = resolvePresetRange(draft.value.rangePreset, new Date());
  draft.value.since = resolved.since;
  draft.value.until = resolved.until;
}

function onOrgSelect(option: ComboOption) {
  draft.value.orgId = option.id;
  draft.value.orgName = option.name;
  // Changing the org invalidates any previously selected team/repo/user: the backend only accepts
  // repositories owned by, and users who are members of, the selected organization.
  draft.value.teamId = 0;
  draft.value.teamName = '';
  draft.value.repoId = 0;
  draft.value.repoName = '';
  draft.value.userId = 0;
  draft.value.userName = '';
}

function onTeamSelect(option: ComboOption) {
  draft.value.teamId = option.id;
  draft.value.teamName = option.name;
  // Same reasoning as onOrgSelect: a team filter narrows the accepted repositories and users down to
  // that team, so a previously picked repository/user may no longer be valid.
  draft.value.repoId = 0;
  draft.value.repoName = '';
  draft.value.userId = 0;
  draft.value.userName = '';
}

function onRepoSelect(option: ComboOption) {
  draft.value.repoId = option.id;
  draft.value.repoName = option.name;
}

function onUserSelect(option: ComboOption) {
  draft.value.userId = option.id;
  draft.value.userName = option.name;
}

function extraSearchParams(kind: ComboKind): Record<string, string> {
  const params: Record<string, string> = {};
  if (props.scopeType === 'global' && draft.value.orgId) params.org = String(draft.value.orgId);
  if ((kind === 'repo' || kind === 'user') && draft.value.teamId) params.team = String(draft.value.teamId);
  return params;
}

/**
 * Builds a debounced, race-safe search controller for one combo box (org/team/repo).
 * Shared by all three combos so the debounce + stale-response handling only lives once.
 */
function createOptionsController(kind: ComboKind) {
  const items = ref<ComboOption[]>([]);
  const loading = ref(false);
  const dropdownOpen = ref(false);
  let requestSeq = 0;

  const search = debounce(280, async (query: string) => {
    if (!props.filtersUrl) {
      items.value = [];
      return;
    }
    const seq = ++requestSeq;
    loading.value = true;
    try {
      const params = new URLSearchParams({kind, q: query, ...extraSearchParams(kind)});
      const response = await GET(`${props.filtersUrl}?${params.toString()}`);
      if (seq !== requestSeq) return; // a newer search has superseded this one, ignore the stale response
      if (!response.ok) {
        items.value = [];
        return;
      }
      const data = await response.json() as {items?: Array<{id: number; name: string; full_name?: string}>};
      items.value = (data.items ?? []).map((item) => ({id: item.id, name: item.full_name || item.name}));
    } catch {
      if (seq === requestSeq) items.value = [];
    } finally {
      if (seq === requestSeq) loading.value = false;
    }
  });

  function setOpen(open: boolean) {
    dropdownOpen.value = open;
  }

  return {items, loading, search, dropdownOpen, setOpen};
}

const orgOptions = createOptionsController('org');
const teamOptions = createOptionsController('team');
const repoOptions = createOptionsController('repo');
const userOptions = createOptionsController('user');

// The panel clips its own overflow so it can animate between collapsed and expanded (a CSS grid
// track transition, see dashboard.css). That clipping also swallowed the combo boxes' absolutely
// positioned result lists, which render below their input and therefore outside the panel box -
// making the org/team/repo search results invisible and unclickable. Only lift the clipping while
// a dropdown is actually open, so the expand/collapse animation itself stays clean.
const dropdownOpen = computed(() => orgOptions.dropdownOpen.value || teamOptions.dropdownOpen.value ||
  repoOptions.dropdownOpen.value || userOptions.dropdownOpen.value);

// Collapsing the panel makes it inert, which blurs whatever combo was focused - but don't rely on
// that alone to restore the panel's overflow clipping, or a leftover "open" flag would keep the
// collapse animation unclipped.
watch(() => props.expanded, (expanded) => {
  if (expanded) return;
  orgOptions.setOpen(false);
  teamOptions.setOpen(false);
  repoOptions.setOpen(false);
  userOptions.setOpen(false);
});

function applyFilters() {
  if (!isRangeValid.value) return;
  emit('apply', {...draft.value});
}

function resetFilters() {
  emit('reset');
}
</script>

<template>
  <div
    class="devops-filter-panel"
    :class="{'devops-filter-panel-expanded': expanded, 'devops-filter-panel-dropdown-open': dropdownOpen}"
    :inert="!expanded"
    :aria-hidden="!expanded"
  >
    <div class="devops-filter-panel-inner">
      <div class="devops-filter-panel-content">
        <DevopsDashboardFilterCombo
          v-if="showOrgFilter"
          :label="locale.filterOrg"
          :placeholder="locale.filterSearchPlaceholder"
          :all-label="locale.filterAll"
          :no-results-label="locale.filterNoResults"
          :disabled="false"
          disabled-hint=""
          :items="orgOptions.items.value"
          :loading="orgOptions.loading.value"
          :selected-id="draft.orgId"
          :selected-name="draft.orgName"
          @search="orgOptions.search"
          @select="onOrgSelect"
          @open-change="orgOptions.setOpen"
        />
        <DevopsDashboardFilterCombo
          v-if="showTeamFilter"
          :label="locale.filterTeam"
          :placeholder="locale.filterSearchPlaceholder"
          :all-label="locale.filterAll"
          :no-results-label="locale.filterNoResults"
          :disabled="teamDisabled"
          :disabled-hint="locale.filterTeamNeedsOrg"
          :items="teamOptions.items.value"
          :loading="teamOptions.loading.value"
          :selected-id="draft.teamId"
          :selected-name="draft.teamName"
          @search="teamOptions.search"
          @select="onTeamSelect"
          @open-change="teamOptions.setOpen"
        />
        <DevopsDashboardFilterCombo
          v-if="showRepoFilter"
          :label="locale.filterRepo"
          :placeholder="locale.filterSearchPlaceholder"
          :all-label="locale.filterAll"
          :no-results-label="locale.filterNoResults"
          :disabled="false"
          disabled-hint=""
          :items="repoOptions.items.value"
          :loading="repoOptions.loading.value"
          :selected-id="draft.repoId"
          :selected-name="draft.repoName"
          @search="repoOptions.search"
          @select="onRepoSelect"
          @open-change="repoOptions.setOpen"
        />
        <DevopsDashboardFilterCombo
          v-if="showUserFilter"
          :label="locale.filterUser"
          :placeholder="locale.filterSearchPlaceholder"
          :all-label="locale.filterAll"
          :no-results-label="locale.filterNoResults"
          :disabled="false"
          disabled-hint=""
          :items="userOptions.items.value"
          :loading="userOptions.loading.value"
          :selected-id="draft.userId"
          :selected-name="draft.userName"
          @search="userOptions.search"
          @select="onUserSelect"
          @open-change="userOptions.setOpen"
        />

        <div class="devops-filter-field devops-filter-range">
          <label class="devops-filter-label">{{ locale.filterRange }}</label>
          <select v-model="draft.rangePreset" class="devops-filter-select" @change="onRangePresetChange">
            <option v-for="preset in RANGE_PRESETS" :key="preset" :value="preset">
              {{ locale[rangePresetLocaleKeys[preset]] }}
            </option>
          </select>
          <div v-if="draft.rangePreset === 'custom'" class="devops-filter-range-custom">
            <label>
              {{ locale.rangeSince }}
              <input v-model="draft.since" type="date" class="devops-filter-date">
            </label>
            <label>
              {{ locale.rangeUntil }}
              <input v-model="draft.until" type="date" class="devops-filter-date">
            </label>
          </div>
          <p v-if="!isRangeValid" class="devops-filter-error">{{ locale.rangeInvalid }}</p>
        </div>

        <div class="devops-filter-field">
          <label class="devops-filter-label">{{ locale.filterTop }}</label>
          <select v-model.number="draft.top" class="devops-filter-select">
            <option v-for="option in topOptionsForSelect" :key="option" :value="option">
              {{ option === 0 ? topAllLabel : option }}
            </option>
          </select>
        </div>

        <div class="devops-filter-actions">
          <button type="button" class="ui button devops-filter-reset" @click="resetFilters">{{ locale.filterReset }}</button>
          <button
            type="button"
            class="ui button primary devops-filter-apply"
            :disabled="!isRangeValid"
            @click="applyFilters"
          >
            {{ locale.filterApply }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
